import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, query } from "../../lib/api";
import { formatDateTime, titleCase } from "../../lib/format";
import type { AuditRecord, ListResponse } from "../../lib/types";
import {
  EmptyState,
  Mono,
  PageHeader,
  Pagination,
  Sheet,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from "../../components/ui";

const PAGE_SIZE = 50;

export function Audit() {
  const [action, setAction] = useState("");
  const [offset, setOffset] = useState(0);

  const audit = useQuery({
    queryKey: ["audit", action, offset],
    queryFn: () =>
      api.get<ListResponse<AuditRecord>>(`/api/admin/audit${query({ action, limit: PAGE_SIZE, offset })}`),
  });

  const rows = audit.data?.data ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Platform"
        title="Audit log"
        description="An immutable record of every privileged action: who did what, to which object, and when."
      />

      <div className="settle settle-1 mb-4">
        <select
          value={action}
          onChange={(e) => {
            setAction(e.target.value);
            setOffset(0);
          }}
          aria-label="Filter by action"
          className="rounded-md border border-rule-strong bg-paper-raised px-2.5 py-1.5 text-[13px]"
        >
          <option value="">All actions</option>
          {[
            "tenant.registered",
            "kyc.submitted",
            "kyc.approved",
            "kyc.rejected",
            "tenant.suspended",
            "tenant.reinstated",
            "api_key.created",
            "api_key.revoked",
            "payout.created",
            "payout.confirmed",
            "payout.failed",
            "pricing_plan.assigned",
            "pricing_plan.created",
          ].map((value) => (
            <option key={value} value={value}>
              {titleCase(value.replace(".", " "))}
            </option>
          ))}
        </select>
      </div>

      <Sheet className="settle settle-2 overflow-hidden">
        {audit.isLoading ? (
          <Skeleton rows={10} />
        ) : rows.length === 0 ? (
          <EmptyState title="Nothing recorded" description="Privileged actions will appear here." />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Actor</Th>
                  <Th>Action</Th>
                  <Th>Target</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((record) => (
                  <Tr key={record.id}>
                    <Td className="whitespace-nowrap text-[12px] text-ink-muted">
                      {formatDateTime(record.created_at)}
                    </Td>
                    <Td>
                      <span className="text-[12.5px] text-ink">
                        {record.actor_email || titleCase(record.actor_type)}
                      </span>
                    </Td>
                    <Td>
                      <span className="font-mono text-[12px] text-ink">{record.action}</span>
                    </Td>
                    <Td>
                      {record.target_id ? (
                        <Mono>{`${record.target_type}/${record.target_id}`}</Mono>
                      ) : (
                        <span className="text-ink-faint">—</span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              total={audit.data?.total ?? 0}
              limit={PAGE_SIZE}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Sheet>
    </>
  );
}
